import { URL_IMG } from '@/constants/ui'
import { getUpsellOffers } from '@/features/payment'
import { TypePopup, useUpsellStore } from '@/store/upsell'
import { getVND } from '@/utils/helpers'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import Image from 'next/image'
import { useCallback, useEffect, useMemo } from 'react'
import { useSubmitOffers } from '../hooks/use-submit-offers'
import { TProductItem, WrapGlobalFixedProduct } from './wrap-global-fixed-product'
import { motion } from 'framer-motion'
import { useUpsellCountdown } from '../hooks/use-upsell-countdown'

//#region FixedUpsell
export const FixedUpsell = () => {
  const { orderCode, clearOrderCode, type } = useUpsellStore()
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['ORDER_OFFER', orderCode],
    queryFn: () => {
      return getUpsellOffers(Number(orderCode))
    },
    enabled: !!orderCode,
    staleTime: 0,
    gcTime: 0,
    retry: 2,
  })

  const clearOrderCodeSafe = useCallback(() => {
    const { orderCode } = useUpsellStore.getState()
    if (orderCode) {
      clearOrderCode()
    }
  }, [clearOrderCode])

  const onExpire = () => {
    clearOrderCodeSafe()
  }

  // Sử dụng custom hook để quản lý countdown
  const { expireAfter } = useUpsellCountdown({
    type: TypePopup.UPSELL,
    expireDate: data?.expire_after_date,
    onComplete: onExpire,
  })

  useEffect(() => {
    if (orderCode && type === TypePopup.UPSELL) {
      refetch()
    }
  }, [orderCode, type, refetch])

  // clear the orderCode when the data is an empty offer list
  useEffect(() => {
    if (data?.offers?.length === 0) {
      clearOrderCodeSafe()
    }
  }, [data?.offers?.length, clearOrderCodeSafe])

  const total = useMemo(() => {
    return (
      data?.offers?.reduce((acc, offer) => {
        return acc + (offer?.final_price || 0) * (offer?.add_qty || 0)
      }, 0) || 0
    )
  }, [data?.offers])

  const { handleSubmit, isPending } = useSubmitOffers(data)
  // // clear the orderCode when the countdown expires
  // useEffect(() => {
  //   const hasValidData = data && data?.offers?.length > 0 && data?.expire_after_date
  //   if (hasValidData && !isLoading && expireAfter <= 0) {
  //     console.log('🔴 Countdown expired, clearing orderCode')
  //     clearOrderCode()
  //   }
  // }, [data, isLoading, expireAfter, clearOrderCode])

  const convertedData: TProductItem[] = useMemo(() => {
    if (!data) return [] as TProductItem[]
    const tempResult = data?.offers?.map((item) => ({
      product_name: item?.product_name,
      add_qty: item?.add_qty,
      final_price: item?.final_price,
      checkout_price: item?.checkout_price,
      product_image: item?.product_image,
      link: '#',
      attributes: item?.attributes || [],
    }))
    return tempResult as TProductItem[]
  }, [data])

  const caculatedCashbackCoin = useMemo(() => {
    const cashback = data?.affiliate_information?.cashback
    if (!cashback || !total) return 0
    const cashbackCoin = Math.ceil((cashback * total) / 100)
    return cashbackCoin
  }, [data?.affiliate_information?.cashback, total])

  const isHidePopup = !data || isLoading || expireAfter <= 0 || data?.offers?.length === 0

  if (isHidePopup) return null

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      sx={{
        position: 'fixed',
        right: 0,
        top: '40vh',
        zIndex: 2,
      }}
    >
      <WrapGlobalFixedProduct
        data={convertedData}
        total={total}
        expireAfter={expireAfter}
        content={{
          buttonSubmit: (
            <ButtonSubmit
              loading={isLoading || isPending}
              onSubmit={handleSubmit}
              cashbackCoin={caculatedCashbackCoin}
              total={total}
              order={orderCode || ''}
            />
          ),
        }}
      />
    </Box>
  )
}

//#region Button Submit
const ButtonSubmit = ({
  cashbackCoin,
  total,
  order,
  onSubmit,
  loading = false,
}: {
  cashbackCoin: number
  total: number
  order: string
  onSubmit: () => void
  loading?: boolean
}) => {
  return (
    <Button
      loading={loading}
      disabled={loading}
      onClick={() => onSubmit()}
      variant="contained"
      sx={{
        width: '100%',
        height: '100%',
        padding: '12px 24px',
        color: 'primary.contrastText',
        textTransform: 'none',
        '&.Mui-disabled': {
          backgroundColor: 'primary.main',
          '& img': {
            opacity: 0.5,
          },
          '& .MuiButton-loadingIndicator': {
            color: 'primary.contrastText',
          },
        },
        '&.MuiButton-loading': {
          '& img': {
            opacity: 0,
          },
        },
      }}
    >
      <Stack>
        <Typography
          sx={{ fontSize: { xs: '0.875rem', md: '1rem', xl: '1.125rem' }, color: 'inherit', fontWeight: 600 }}
        >
          THANH TOÁN NGAY - {getVND(total, 'Đ')}
        </Typography>
        {cashbackCoin > 0 && (
          <Box>
            <Typography
              component={'span'}
              sx={{
                fontSize: { xs: '12px', md: '14px' },
                lineHeight: 1.3,
                color: 'inherit',
                fontWeight: 300,
              }}
            >
              Nhận ngay{' '}
              <Box component={'span'} sx={{ fontWeight: 600 }}>
                {getVND(cashbackCoin, ' ')}
              </Box>{' '}
              xu
            </Typography>
            <Image
              style={{ display: 'inline-block', margin: '0 4px' }}
              width={16}
              height={16}
              src={URL_IMG + 'favicon-yellow.svg'}
              alt="Favicon"
            />
          </Box>
        )}
      </Stack>
    </Button>
  )
}
